import type { PostBlock, PostDoc, PostInline, PostListItem } from "@baze/schema";
import { Fragment, type ReactNode } from "react";
import type { RenderContext, SectionProps } from "./context";
import { imageSrc, safeHref, safeImageSrc, srcSet } from "./helpers";
import { Icon } from "./icons";
import { BUTTON, CARD_HOVER, Container, Img, MultiLine, REVEAL, STAGGER, SectionHeader, SectionShell, SiteLink, cx, enterDelay } from "./ui";

/**
 * Blog rendering. Posts are not part of the Website Definition: the web runtime fetches them and passes
 * plain data in (ctx.posts for the blog-latest section, props for the /blog pages). Bodies are the API's
 * normalized Tiptap JSON (see @baze/schema post.ts), rendered node by node; URLs are re-checked here.
 */

/** What a post card needs (PostSummary in the API contract). */
export interface BlogCardData {
  slug: string;
  title: string;
  excerpt: string;
  cover?: { src: string; alt: string; width?: number; height?: number };
  publishedAt?: string;
}

export interface BlogArticleData extends BlogCardData {
  body: PostDoc;
}

const dateFormat = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jakarta" });

export function postDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : dateFormat.format(d);
}

/** /blog/<slug>, only for slugs the API could have produced ("halaman" is the pagination route). */
export function postHref(slug: string): string | null {
  return /^[a-z0-9][a-z0-9-]{0,79}$/.test(slug) && slug !== "halaman" ? `/blog/${slug}` : null;
}

const SAMPLE_POSTS: BlogCardData[] = [
  { slug: "contoh-1", title: "Tips memilih layanan yang tepat untuk kebutuhan Anda", excerpt: "Panduan singkat agar Anda tidak salah pilih: apa yang perlu ditanyakan sebelum memesan.", publishedAt: "2026-10-01T03:00:00Z" },
  { slug: "contoh-2", title: "Cerita di balik usaha kami", excerpt: "Bagaimana kami memulai, apa yang kami pelajari, dan mengapa kami tetap bertahan.", publishedAt: "2026-09-18T03:00:00Z" },
  { slug: "contoh-3", title: "Kabar terbaru dan agenda bulan ini", excerpt: "Jadwal, promo, dan kegiatan yang bisa Anda ikuti dalam waktu dekat.", publishedAt: "2026-09-02T03:00:00Z" },
];

// ── Cards ─────────────────────────────────────────────────────────────────

function PostCard({ ctx, post, list, index }: { ctx: RenderContext; post: BlogCardData; list?: boolean; index: number }) {
  const href = ctx.editor ? null : postHref(post.slug);
  const date = postDate(post.publishedAt);
  const inner = (
    <>
      {post.cover && !list ? (
        <div className="aspect-[16/10] overflow-hidden rounded-site-md bg-site-surface">
          <Img ctx={ctx} image={post.cover} sizes="(min-width: 1024px) 33vw, 100vw" target={960} />
        </div>
      ) : null}
      <div className={cx(!list && post.cover ? "mt-4" : "")}>
        {date ? <p className="text-xs font-semibold tracking-wide text-site-muted uppercase">{date}</p> : null}
        <h3 className="baze-heading mt-1.5 text-lg leading-snug text-site-text @md:text-xl">{post.title}</h3>
        {post.excerpt ? <p className="mt-2 line-clamp-3 leading-relaxed text-site-muted">{post.excerpt}</p> : null}
      </div>
    </>
  );
  const cls = cx(
    "group block h-full",
    list ? "border-b border-site-border py-6 first:pt-0" : cx("rounded-site-lg border border-site-border bg-site-surface p-4 shadow-site-sm @md:p-5", CARD_HOVER),
  );
  return (
    <li style={enterDelay(index)}>
      {href ? (
        <SiteLink ctx={ctx} href={href} className={cls}>
          {inner}
        </SiteLink>
      ) : (
        <div className={cls}>{inner}</div>
      )}
    </li>
  );
}

/** Section "Artikel Terbaru". Live with no posts it renders nothing. */
export function BlogLatest({ node, ctx }: SectionProps<"blog-latest">) {
  const c = node.content;
  const count = c.count ?? 3;
  const posts = (ctx.editor || ctx.thumbnail ? (ctx.posts?.length ? ctx.posts : SAMPLE_POSTS) : (ctx.posts ?? [])).slice(0, count);
  if (!posts.length) return null;
  const list = node.variant === "list";
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size={list ? "narrow" : "default"}>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align={list ? "left" : "center"} />
        <MultiLine ctx={ctx} text={c.description} path="content.description" className={cx("mt-4 leading-relaxed text-site-muted", !list && "mx-auto max-w-2xl text-center")} />
        <ul className={cx(REVEAL, STAGGER, "mt-10", list ? "" : "grid gap-5 @2xl:grid-cols-2 @4xl:grid-cols-3")}>
          {posts.map((p, i) => (
            <PostCard key={p.slug} ctx={ctx} post={p} list={list} index={i} />
          ))}
        </ul>
        <div className={cx("mt-10 flex", list ? "" : "justify-center")}>
          <SiteLink ctx={ctx} href="/blog" className={BUTTON.secondary}>
            <span>{c.linkLabel?.trim() || "Semua artikel"}</span>
            <Icon name="arrow-right" className="size-4" />
          </SiteLink>
        </div>
      </Container>
    </SectionShell>
  );
}

/** /blog: every published post, newest first, with simple numbered pagination. */
export function BlogIndex({ ctx, title, posts, page, pages }: { ctx: RenderContext; title: string; posts: BlogCardData[]; page: number; pages: number }) {
  return (
    <section className="baze-section bg-site-bg py-(--site-section-y) text-site-text @3xl:py-(--site-section-y-lg)">
      <Container>
        <h1 className="baze-heading text-center text-4xl @3xl:text-5xl">{title}</h1>
        {posts.length ? (
          <ul className={cx(STAGGER, "mt-12 grid gap-5 @2xl:grid-cols-2 @4xl:grid-cols-3")}>
            {posts.map((p, i) => (
              <PostCard key={p.slug} ctx={ctx} post={p} index={i} />
            ))}
          </ul>
        ) : (
          <p className="mt-10 text-center text-site-muted">Belum ada artikel.</p>
        )}
        {pages > 1 ? (
          <nav aria-label="Halaman artikel" className="mt-12 flex flex-wrap justify-center gap-2">
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <SiteLink
                key={n}
                ctx={ctx}
                href={n === 1 ? "/blog" : `/blog/halaman/${n}`}
                className={cx(
                  "grid size-11 place-items-center rounded-full border text-sm font-semibold transition-colors",
                  n === page ? "border-site-primary bg-site-primary text-site-on-primary" : "border-site-border text-site-text hover:border-site-primary",
                )}
              >
                <span aria-current={n === page ? "page" : undefined}>{n}</span>
              </SiteLink>
            ))}
          </nav>
        ) : null}
      </Container>
    </section>
  );
}

/** /blog/<slug>: one article. */
export function BlogArticle({ ctx, post }: { ctx: RenderContext; post: BlogArticleData }) {
  const date = postDate(post.publishedAt);
  return (
    <article className="baze-section bg-site-bg py-(--site-section-y) text-site-text @3xl:py-(--site-section-y-lg)">
      <Container size="narrow">
        <SiteLink ctx={ctx} href="/blog" className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-site-muted hover:text-site-primary">
          <Icon name="chevron-left" className="size-4" />
          <span>Semua artikel</span>
        </SiteLink>
        <header className="mt-4">
          {date ? (
            <p className="text-sm font-semibold text-site-muted">
              <time dateTime={post.publishedAt}>{date}</time>
            </p>
          ) : null}
          <h1 className="baze-heading mt-2 text-3xl leading-tight @3xl:text-5xl">{post.title}</h1>
        </header>
        {post.cover ? (
          <div className="mt-8 aspect-[16/9] overflow-hidden rounded-site-lg bg-site-surface">
            <Img ctx={ctx} image={post.cover} sizes="(min-width: 768px) 720px, 100vw" target={1600} priority />
          </div>
        ) : null}
        <div className="baze-prose mt-10">
          <PostBody doc={post.body} />
        </div>
      </Container>
    </article>
  );
}

// ── Body ──────────────────────────────────────────────────────────────────

const BLOCK = {
  p: "my-5 text-[1.0625rem] leading-[1.8] text-site-text/90",
  h2: "baze-heading mt-12 mb-4 text-2xl leading-snug text-site-text @3xl:text-3xl",
  h3: "baze-heading mt-9 mb-3 text-xl leading-snug text-site-text",
  ul: "my-5 list-disc space-y-2 pl-6 text-[1.0625rem] leading-[1.8] marker:text-site-primary",
  ol: "my-5 list-decimal space-y-2 pl-6 text-[1.0625rem] leading-[1.8] marker:text-site-primary",
  quote: "my-8 border-l-4 border-site-primary pl-5 text-site-muted italic",
  hr: "my-12 border-site-border",
  figure: "my-8",
} as const;

/** Renders a normalized post body. Unknown nodes (an older or newer API) are skipped, never guessed. */
export function PostBody({ doc }: { doc: PostDoc }) {
  return <>{blocks(doc?.content)}</>;
}

function blocks(list: ReadonlyArray<PostBlock> | undefined): ReactNode[] {
  return (list ?? []).map((b, i) => <Fragment key={i}>{block(b)}</Fragment>);
}

function block(b: PostBlock): ReactNode {
  switch (b.type) {
    case "paragraph":
      return b.content?.length ? <p className={BLOCK.p}>{inline(b.content)}</p> : null;
    case "heading":
      return b.attrs?.level === 3 ? <h3 className={BLOCK.h3}>{inline(b.content)}</h3> : <h2 className={BLOCK.h2}>{inline(b.content)}</h2>;
    case "bulletList":
      return <ul className={BLOCK.ul}>{items(b.content)}</ul>;
    case "orderedList": {
      const start = b.attrs?.start;
      return (
        <ol className={BLOCK.ol} start={typeof start === "number" && start > 1 ? start : undefined}>
          {items(b.content)}
        </ol>
      );
    }
    case "blockquote":
      return <blockquote className={BLOCK.quote}>{blocks(b.content)}</blockquote>;
    case "horizontalRule":
      return <hr className={BLOCK.hr} />;
    case "image": {
      const src = safeImageSrc(b.attrs?.src);
      if (!src) return null;
      const image = { src, alt: b.attrs.alt ?? "" };
      const set = srcSet(image);
      return (
        <figure className={BLOCK.figure}>
          <img src={imageSrc(image, 1600) ?? src} srcSet={set} sizes={set ? "(min-width: 768px) 720px, 100vw" : undefined} alt={image.alt} loading="lazy" decoding="async" className="w-full rounded-site-md" />
          {image.alt ? <figcaption className="mt-2 text-center text-sm text-site-muted">{image.alt}</figcaption> : null}
        </figure>
      );
    }
    default:
      return null;
  }
}

function items(list: ReadonlyArray<PostListItem> | undefined): ReactNode[] {
  return (list ?? []).map((li, i) => (
    // List items hold paragraphs; unwrap them so bullets sit on the text line.
    <li key={i}>{(li.content ?? []).map((c, j) => <Fragment key={j}>{c.type === "paragraph" ? inline(c.content) : block(c)}</Fragment>)}</li>
  ));
}

function inline(list: ReadonlyArray<PostInline> | undefined): ReactNode[] {
  return (list ?? []).map((n, i) => {
    if (n.type === "hardBreak") return <br key={i} />;
    if (n.type !== "text") return null;
    let node: ReactNode = n.text;
    for (const m of n.marks ?? []) {
      if (m.type === "bold") node = <strong>{node}</strong>;
      else if (m.type === "italic") node = <em>{node}</em>;
      else if (m.type === "link") {
        const href = safeHref(m.attrs?.href);
        if (!href) continue;
        const external = /^https?:/.test(href);
        node = (
          <a href={href} className="font-medium text-site-primary underline underline-offset-2" {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
            {node}
          </a>
        );
      }
    }
    return <Fragment key={i}>{node}</Fragment>;
  });
}
